import React, { useReducer, useEffect, useState } from "react";
import Button from "../../../components/Button";
import {
  ButtonsContainer,
  TableRow,
  TBody,
  THead,
  Table,
  TableContainer
} from "../../../components/Table";
import { ChartContainer } from "../../../components/Charts";
import Chart from "react-apexcharts";
import EditableLabel from "../../../components/EditableLabel";
import { mockValues } from "../../../test/mock/page_rooms";
import { parseField } from "../../../utils/utils";
import KPIDisplay from "../../../components/KPIDisplay";

const initialState = {
  months: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  number_of_rooms: ["0", "0", "0", "0", "0", "0", "0", "0", "0", "0", "0", "0"],
  occupancy: ["0.00", "0.00", "0.00", "0.00", "0.00", "0.00", "0.00", "0.00", "0.00", "0.00", "0.00", "0.00"],
  adr: [
    "0.00",
    "0.00",
    "0.00",
    "0.00",
    "0.00",
    "0.00",
    "0.00",
    "0.00",
    "0.00",
    "0.00",
    "0.00",
    "0.00",
  ],
  revpar: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  total_revenue_line: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
};

const has31Days = ["Jan", "Mar", "May", "Jul", "Aug", "Oct", "Dec"];
const has30Days = ["Apr", "Jun", "Sep", "Nov"];

const reducer = (state, action) => {
  const { index, value } = action.payload;
  switch (action.type) {
    case "number_of_rooms":
      const number_of_rooms = state.number_of_rooms;
      number_of_rooms[index] = value;
      return { ...state, number_of_rooms };
    case "occupancy":
      const occupancy = state.occupancy;
      occupancy[index] = value;
      return { ...state, occupancy };
    case "adr":
      const adr = state.adr;
      adr[index] = value;
      return { ...state, adr };
    case "revpar":
      const revpar = state.revpar;
      revpar[index] = value;
      return { ...state, revpar };
    case "total_revenue_line":
      const total_revenue_line = state.total_revenue_line;
      total_revenue_line[index] = value;
      return { ...state, total_revenue_line };
    default:
      throw new Error("Invalid action");
  }
};

export default function TableRooms({
  nextPage,
  handleError,
  sumElements,
  calculateAverage,
  setTotalRevenueArray,
  totalRevenueArray,
  globalIndicators,
  updateGlobalIndicators
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isBigTd, setIsBigTd] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);

  useEffect(() => {
    // Set isInitialRender to false after first render
    setIsInitialRender(false);
  }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleChange = (obj) => {
    const { index, value, type } = obj;
    try {
      if (!value) {
        throw new Error("Invalid value");
      }
      switch (type) {
        case "number_of_rooms":
          dispatch({
            type,
            payload: { index, value: parseField(value, "integer") },
          });
          break;
        case "occupancy":
          dispatch({
            type,
            payload: { index, value: parseField(value, "percentage") },
          });
          break;
        case "adr":
          dispatch({
            type,
            payload: { index, value: parseField(value, "full-money") },
          });
          break;
        default:
          throw new Error("Invalid action");
      }

      let month = state.months[index];
      let days = has31Days.includes(month) ? 31 : has30Days.includes(month) ? 30 : 28;
      let nRooms = state.number_of_rooms[index];
      let occ = Number(state.occupancy[index]);
      let adrNum = Number(state.adr[index].split(",").join(""));
      
      const revenue = (((nRooms * days) * occ) / 100 * adrNum);
      const revpar = (revenue / (nRooms * days || 1));

      dispatch({
        type: "total_revenue_line",
        payload: { index, value: revenue }
      });
      
      dispatch({
        type: "revpar",
        payload: { index, value: revpar }
      });

      const newTotalRevenueArray = [...totalRevenueArray];
      newTotalRevenueArray[index] = revenue;
      setTotalRevenueArray(newTotalRevenueArray);

      const totalRevenue = newTotalRevenueArray.reduce(sumElements, 0);

      updateGlobalIndicators(
        totalRevenue,
        globalIndicators.revenueOthers,
        globalIndicators.totalExpenses
      );

      const topTotal = newTotalRevenueArray.reduce((max, val) => Math.max(max, val), 0);
      setIsBigTd(topTotal.toString().length > 6);
    } catch (err) {
      handleError(err.message);
    }
  };


  return (
    <TableContainer>
      <Table>
        <THead>
          <tr>
            <td>
            </td>

            {state?.months?.map((item) => (
              <td key={`hd-${item}`}>{item}</td>
            ))}

            <td className="total"></td>
          </tr>
        </THead>
        <TBody>
          <TableRow>
            <td>Rooms</td>
            {state?.number_of_rooms?.map((_, index) => (
              <td key={`tb-rooms-num_rooms-${index}`} id={`td-rooms-${index}`}>

                <EditableLabel
                  value={parseField("" + state.number_of_rooms[index], "integer")}
                  type="integer"
                  onFocusValue={state.number_of_rooms[index]}
                  setValue={(item) =>
                    handleChange({
                      type: "number_of_rooms",
                      index,
                      value: item,
                    })
                  }
                  autoFocus={isInitialRender && index === 0}
                />

              </td>
            ))}
            <td title="Average" className="total-result">
              {Math.round(100 * (calculateAverage(state?.number_of_rooms) / 100))}
            </td>
          </TableRow>
          <TableRow>
            <td>Occ (%)</td>
            {state?.occupancy?.map((_, index) => (
              <td key={`tb-rooms-occupancy-${index}`} id={`#td-occupancy-${index}`}>
                <EditableLabel
                  hasScallableWidth={true}
                  type="percentage"
                  value={parseField("" + state.occupancy[index], "percentage")}
                  onFocusValue={state.occupancy[index]}
                  setValue={(item) =>
                    handleChange({
                      type: "occupancy",
                      index,
                      value: item,
                    })
                  }
                /> %
              </td>
            ))}
            <td title="Average" className="total-result">
              {Math.round(calculateAverage(state?.occupancy) * 100) / 100} %
            </td>
          </TableRow>
          <TableRow>
            <td>ADR</td>
            {state?.adr?.map((_, index) => (
              <td key={`tb-rooms-adr-${index}`}>
                <div>
                  <span>$</span>
                  <EditableLabel
                    value={parseField("" + state.adr[index], "money")}
                    type="money"
                    setValue={(item) =>
                      handleChange({
                        type: "adr",
                        index,
                        value: item,
                      })
                    }
                    onFocusValue={state.adr[index]}
                  />
                </div>
              </td>
            ))}
            <td 
              title={"$" + parseField("" + Math.round(calculateAverage(state.adr) * 100) / 100, "full-money")} 
              className="total-result"
            >
              ${parseField(""+Math.round(calculateAverage(state.adr) * 100) / 100, "money")}
            </td>
          </TableRow>
          <TableRow>
            <td style={{ fontWeight: "bold" }}>RevPAR</td>
            {state?.revpar.map((_, index) => (
              <td 
                title={"$" + parseField("" + state?.revpar[index], "full-money")} 
                className={"total-result-number"} 
                key={`revpar-td-${index}`}
              >
                {"$" + parseField("" + state?.revpar[index], "money")}
              </td>
            ))}  
            <td
              title={"$" + 
                parseField("" + 
                  (state?.revpar.reduce(sumElements, 0) / 12),
                  "full-money"
                )}
              className="total-result total-result-number"
            >
              {"$" + 
                parseField("" + 
                  (state?.revpar.reduce(sumElements, 0) / 12),
                  "money"
                )}
            </td>
          </TableRow>
          <TableRow>
            <td style={{ fontWeight: "bold" }}>Total</td>
            {state?.total_revenue_line.map((_, index) => (
              <td 
                title={"$" + parseField("" + state.total_revenue_line[index], "full-money")} 
                className={"total-result-number"} 
                key={`total-td-${index}`}
              >
                {"$" + parseField("" + state.total_revenue_line[index], "money")}
              </td>
            ))}  
            <td>
            </td>
          </TableRow>
        </TBody>
      </Table>

      <KPIDisplay 
        property={"$" + parseField("" + globalIndicators.propertyValue || 0, "full-money")}
        expenses={"$" + parseField("" + globalIndicators.totalExpenses || 0, "full-money")}
        income={"$" + parseField("" + globalIndicators.netIncome || 0, "full-money")}
        revenue={"$" + parseField("" + globalIndicators.totalRevenue || 0, "full-money")}
      />

      <ChartContainer>
        <Chart
          options={{
            chart: { 
              id: "basic-bar",
              toolbar: {
                show: true,
                tools: {
                  download: false
                }
              },
              fontFamily: "'Noto Sans', sans-serif",
              zoom: {
                enabled: true,
                allowMouseWheelZoom: false,
              }
            },
            markers: { size: 2 },
            xaxis: {
              categories: state.months,
            },
            yaxis: {
              labels: { formatter: (val) => parseField(val.toString(), "money") },
              max: function(max) {
                return max * 1.2;
              }
            },
            animations: {
              enabled: true,
              easing: "linear",
              dynamicAnimation: {
                speed: 1000,
              },
            },
            colors: ['#2352fe'],
            tooltip: {
              custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                return `<div class="tooltip_chart">
                  Total Revenue: <span>$ ${parseField("" + series[seriesIndex][dataPointIndex], "money")}</span>
                </div>`
              }
            }
          }}
          series={[
            {
              name: "Total Revenue",
              data: state.total_revenue_line.map((item) => Number(item)),
            }
          ]}
          type="line"
          width="100%"
          height="100%"
        />
      </ChartContainer>
      <ButtonsContainer>
        <Button label="Next" variant="primary" onClick={() => nextPage({ ...state })} />
      </ButtonsContainer>
    </TableContainer >
  );
}